import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Member } from '@/models/Member';
import { MembersService } from '@/services/domain/MembersService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Button, ButtonText } from '@/components/gluestack/button';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { Key02Icon } from '@hugeicons-pro/core-stroke-rounded';

interface RemoveUnlockButtonProps {
  member: Member;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hideIcon?: boolean;
  /**
   * When provided, the button will also remove the member from the specified
   * query lists after a successful removal. Use a common prefix like 'members'.
   */
  cacheQueryKeyPrefix?: string;
}

export function RemoveUnlockButton({ member, className, size = 'sm', hideIcon = false, cacheQueryKeyPrefix }: RemoveUnlockButtonProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { removeCacheItem } = useQueryCacheUtils();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRemove = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MembersService.removePhotoUnlock(member.Id);

      // Optionally remove from cached lists
      if (cacheQueryKeyPrefix) {
        removeCacheItem(cacheQueryKeyPrefix, member.Id, 'MembersBasic');
      }

      showSuccessToast(t('settings.privacy.removeUnlock.success'));
    } catch (error) {
      showErrorToast(t('settings.privacy.removeUnlock.error'), error as any);
    } finally {
      setIsLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <ButtonWrapper
        icon={hideIcon ? undefined : Key02Icon}
        text={t('settings.privacy.removeUnlock.action')}
        size={size}
        action="primary"
        variant="solid"
        className={className}
        onPress={() => setShowConfirm(true)}
        isDisabled={isLoading}
      />

      <AlertDialog isOpen={showConfirm} onClose={() => setShowConfirm(false)} size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('settings.privacy.removeUnlock.title')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('settings.privacy.removeUnlock.confirmMessage', { userName: member.UserName })}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button variant="outline" action="neutral" onPress={() => setShowConfirm(false)} disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="primary" onPress={handleRemove} disabled={isLoading}>
              <ButtonText>{t('common.continue')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


